import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconCircleCheck, IconCircleX, IconInfoCircle } from "@tabler/icons";

// Colours below are hard-coded to Mantine's own default palette (shade 6/7 of each colour) rather
// than looked up via theme.colors.<name>[shade]. Looking them up from theme occasionally throws
// "Cannot read properties of undefined" if these notifications render before/outside a fully
// resolved Mantine theme context (e.g. right after creating an employee), so using plain hex
// values here avoids that crash entirely.
const WHITE = "#ffffff";

// Exposed as a CSS variable on the document root by App.js, so this always reflects the current
// company's own accent colour without needing access to theme context at all.
const ACCENT = "var(--app-accent-color, #228be6)";

// Mantine's Notification component always wraps whatever `icon` you give it in its own small
// circle (with a background colour it derives from theme) - overriding that circle's background
// here to plain white makes it disappear into the notification's own white background, so all
// that's left visible is the icon glyph itself.
const iconContainerStyles = { backgroundColor: WHITE };

// A "filled" icon, coloured with the company's accent colour rather than a fixed per-status
// colour - success/warning/error/info are told apart by the icon's shape (check/cross/triangle/i),
// not its colour, keeping every notification on-brand. Tabler's icons are outline-only, so this is
// achieved by filling the shape with the accent colour and stroking the line details in white,
// giving the same look as a true "filled" icon variant.
const statusIcon = (Icon) => <Icon size="1.6rem" color={WHITE} fill={ACCENT} stroke={2.25} />;

// Average adult silent-reading speed is ~200-250 words/minute; 200 is used here to stay on the
// generous side so notifications don't disappear before someone's actually finished reading them.
const READING_WORDS_PER_MINUTE = 200;
const MIN_DISPLAY_MS = 4000;

const readingDurationMs = (...parts) => {
  const wordCount = parts
    .filter(Boolean)
    .join(" ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(MIN_DISPLAY_MS, Math.round((wordCount / READING_WORDS_PER_MINUTE) * 60000));
};

const baseStyles = (color) => ({
  root: {
    backgroundColor: WHITE,
    borderColor: `${color}33`,
    // The coloured bar Mantine renders down the notification's left edge - previously painted
    // white to stand out against a solid coloured background, now painted the status colour to
    // stand out against the white one instead.
    "&::before": { backgroundColor: color },
  },
  icon: iconContainerStyles,
  title: { color: ACCENT, fontWeight: 700 },
  description: { color: ACCENT },
  closeButton: {
    color: ACCENT,
    "&:hover": { backgroundColor: `${color}1a` },
  },
});

export function ShowNotificationSuccess(title, message, onClose) {
  const TEAL = "#12b886";
  notifications.show({
    title: title,
    message: message,
    icon: statusIcon(IconCircleCheck),
    radius: "lg",
    autoClose: readingDurationMs(title, message),
    onClose: onClose,
    withCloseButton: false,
    styles: () => baseStyles(TEAL),
  });
}

export function ShowNotificationWarning(title, message, onClose) {
  const ORANGE = "#fd7e14";
  notifications.show({
    title: title,
    message: message,
    icon: statusIcon(IconAlertTriangle),
    radius: "lg",
    autoClose: readingDurationMs(title, message),
    onClose: onClose,
    styles: () => baseStyles(ORANGE),
  });
}

export function ShowNotificationInfo(title, message, onClose) {
  const BLUE = "#228be6";
  notifications.show({
    title: title,
    message: message,
    icon: statusIcon(IconInfoCircle),
    radius: "lg",
    autoClose: readingDurationMs(title, message),
    onClose: onClose,
    styles: () => baseStyles(BLUE),
  });
}

export function ShowNotificationError(title, message, onClose) {
  const RED = "#fa5252";
  notifications.clean();
  notifications.show({
    title: title,
    message: message,
    icon: statusIcon(IconCircleX),
    radius: "lg",
    autoClose: false,
    withCloseButton: true,
    onClose: onClose,
    styles: () => baseStyles(RED),
  });
}

